import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid, Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { SectionHeading } from '../../../components/SectionHeading';
import { NewCollaboratorForm, useNewCollaboratorForm } from '../hooks/useNewCollaboratorForm';
import api from '../../../services/api';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Branch } from '../../../domain/branch';
import { Required } from '../../../components/Required';
import { Controller } from 'react-hook-form';
import PhoneInput from '../../../components/PhoneInput';

export const AddCollaborator = () => {

  const { controls, handleSubmit, formState: { errors }, control } = useNewCollaboratorForm();
  const toast = useToast();
  const navigate = useNavigate();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [errorBranches, setErrorBranches] = useState<string | null>(null);
  const [loadingCollaborator, setLoadingCollaborator] = useState<boolean>();

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        // Antes apuntaba por error a '/collaborators-view/create-collaborator' — la
        // misma ruta que usa el POST de creación (línea de abajo) — y un GET ahí
        // tronaba con 500 apenas se montaba la pantalla. Esta es la ruta de solo
        // lectura que ya usan Transacciones/Links de Pago para listar sucursales.
        const response = await api.get('/transactions-view/branches');
        // Este endpoint responde con un arreglo plano, no envuelto en { content }.
        setBranches(Array.isArray(response.data) ? response.data : response.data.content ?? []);
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorBranches('Error al cargar las sucursales');
        } else {
          setErrorBranches('Ocurrió un error desconocido');
        }
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, []);

  const submit = async (formData: NewCollaboratorForm) => {
    setLoadingCollaborator(true)
    try {
      await api.post('/collaborators-view/create-collaborator', formData);
      toast({
        status: 'success',
        title: 'Operación exitosa',
        position: 'bottom-right',
        description: 'El colaborador ha sido creado correctamente.',
      });
      navigate('../', { state: { shouldReload: true } });
    } catch (e) {
      if (e instanceof AxiosError && e.status === 409) {
        toast({
          position: 'bottom-right',
          status: 'error',
          title: 'Error',
          description: `El colaborador no ha sido creado, el email: "${formData.email}" ya ha sido registrado.`,
        });
        return;
      }

      toast({
        position: 'bottom-right',
        status: 'error',
        title: 'Error',
        description: 'El colaborador no ha sido creado, espere un momento e intente nuevamente.',
      });
    } finally {
      setLoadingCollaborator(false);
    }
  };

  return (
    <>
      <Heading as="h2" fontSize="xl">Colaboradores</Heading>

      <form onSubmit={handleSubmit(submit)} >
        <Center >
          <Box maxWidth="50em" maxHeight="60vh" overflowY="auto">
            <SectionHeading label="Agregar un nuevo colaborador" />
            <Text fontWeight="bold">Agrega sus datos</Text>
            <Text fontSize="s" marginBottom="xxl" color="grey.600">Completa los datos de tu nuevo colaborador</Text>

            <SimpleGrid columns={2} spacing="xl">
              <FormControl isInvalid={!!errors.firstname}>
                <FormLabel>Nombre <Required /></FormLabel>
                <Input type="text" {...controls.firstname} />
                <FormErrorMessage>{errors.firstname?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.lastname}>
                <FormLabel>Apellido <Required /></FormLabel>
                <Input type="text" {...controls.lastname} />
                <FormErrorMessage>{errors.lastname?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Correo electrónico <Required /></FormLabel>
                <Input type="text" {...controls.email} />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.phone_number}>
                <FormLabel>Teléfono de contacto</FormLabel>
                <Controller
                  name="phone_number"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  )}
                />
                <FormErrorMessage color="red.500"
                                  fontSize="s" whiteSpace="normal"
                                  wordBreak="break-word" maxWidth="300px">
                  {errors.phone_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.id_branch}>
                <FormLabel>Sucursal asociada<Required /></FormLabel>
                {loadingBranches ? (
                  <Text>Cargando sucursales...</Text>
                ) : errorBranches ? (
                  <Text color="red.500">{errorBranches}</Text>
                ) : (
                  <Select placeholder="Selecciona una sucursal" {...controls.id_branch}>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.fantasy_name}
                      </option>
                    ))}
                  </Select>
                )}
                <FormErrorMessage>{errors.id_branch?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.document_number}>
                <FormLabel>Número de documento de identidad</FormLabel>
                <Input type="text" {...controls.document_number} />
                <FormErrorMessage>{errors.document_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel>Contraseña<Required /></FormLabel>
                <Input type="password" {...controls.password} />
                <FormHelperText color="red.500" fontSize="s">{errors.password?.message}</FormHelperText>
                <FormHelperText fontSize="s" color="grey.700">Mínimo 8 caracteres, una mayúscula y un
                  número.</FormHelperText>
              </FormControl>

              <FormControl isInvalid={!!errors.confirm_password}>
                <FormLabel>Confirmar contraseña<Required /></FormLabel>
                <Input type="password" {...controls.confirm_password} />
                <FormErrorMessage>{errors.confirm_password?.message}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <Stack marginTop="xxxl">
              <FormControl>
                <FormLabel>Descripción de puesto</FormLabel>
                <Input type="text" {...controls.role_description} />
              </FormControl>
            </Stack>
          </Box>
        </Center>

        <Flex justifyContent="flex-end" marginTop="xxxl">
          <Button onClick={() => navigate('/collaborators')}>Cancelar</Button>
          <Button marginLeft="l" colorScheme="button-primary" type="submit" disabled={loadingCollaborator}>
            {loadingCollaborator
              ? <Spinner />
              : 'Agregar nuevo colaborador'
            }
          </Button>
        </Flex>
      </form>
    </>
  );
};
